import { formatContextSizeMessage, estimateContextTokens } from "./contextSize";
import type {
  AgentRunIngestPayload,
  AgentRunMemoryIngestInput,
  AgentRunRecord,
  AgentRunStepKind,
  AgentRunToolCallRecord,
  MemoryTelemetryEvent,
} from "./types";

export type AdminLogLevel = "DEBUG" | "INFO" | "WARN" | "ERROR";

export type AdminLogDetails =
  | {
    kind: "context";
    estimatedTokens: number;
    conversationHistoryCount: number;
    rollingSummary: boolean;
    rollingSummaryChars: number;
    turbopufferMemoryCount: number;
    hasEventContext: boolean;
    guestDetail?: string;
  }
  | {
    kind: "tool_call";
    name: string;
    argsPreview: string;
    resultChars: number;
    resultPreview?: string;
    error?: string;
  }
  | {
    kind: "step";
    step: AgentRunStepKind;
    detail?: string;
  }
  | {
    kind: "run_start";
    inboundPreview: string;
  }
  | {
    kind: "stacked";
    stackedCount: number;
  }
  | {
    kind: "run_complete";
    latencyMs: number;
  }
  | {
    kind: "run_fail";
    status: "failed" | "fallback";
    error: string;
  }
  | {
    kind: "memory";
    event: MemoryTelemetryEvent;
    contactId?: string;
    eventId?: string;
    messageCount?: number;
    summaryChars?: number;
    deletedMessageCount?: number;
    idleThresholdMs?: number;
    sweepIntervalMs?: number;
    error?: string;
  };

export type AdminLogEntry = {
  id: string;
  ts: string;
  level: AdminLogLevel;
  message: string;
  runId?: string;
  phone?: string;
  details?: AdminLogDetails;
};

export const matchesPhoneFilter = (phone: string | undefined, query: string) => {
  const digits = query.replace(/\D/g, "");

  if (!digits) return true;

  const stored = phone?.replace(/\D/g, "") ?? "";

  return stored.includes(digits);
};

const shortId = (runId: string) => runId.slice(0, 8);

const formatLatency = (latencyMs: number) => {
  if (latencyMs < 1000) return `${latencyMs}ms`;

  return `${(latencyMs / 1000).toFixed(1)}s`;
};

const formatToolMessage = (tool: AgentRunToolCallRecord) => {
  const base = `Tool ${tool.name}(${tool.argsPreview}) → ${tool.resultChars} chars`;

  if (tool.error) {
    return `${base} — error: ${tool.error}`;
  }

  if (tool.resultPreview) {
    return `${base} — ${tool.resultPreview}`;
  }

  return base;
};

const stepLabels: Record<AgentRunStepKind, string> = {
  marked_read: "Marked read",
  memory_flush: "Memory flush",
  llm_start: "LLM started",
  llm_round: "LLM round",
  empty_reply_retry: "Empty reply retry",
  promise_reply_retry: "Promise reply retry",
  gmi_fallback: "GMI fallback",
  tool_budget_exhausted: "Tool budget exhausted",
  blocked_invite_claim: "Blocked invite claim",
  delivery_tapback: "Tapback sent",
  delivery_text: "Text sent",
  delivery_link: "Link sent",
  memory_extraction_scheduled: "Memory extraction scheduled",
  rolling_summary_update: "Rolling summary update",
  fallback_sent: "Fallback sent",
};

const stepLevel = (step: AgentRunStepKind, detail?: string): AdminLogLevel => {
  if (
    step === "gmi_fallback"
    || step === "tool_budget_exhausted"
    || step === "blocked_invite_claim"
    || step === "empty_reply_retry"
    || step === "promise_reply_retry"
    || step === "fallback_sent"
  ) {
    return "WARN";
  }

  if (
    step === "delivery_tapback"
    || step === "delivery_text"
    || step === "delivery_link"
    || step === "rolling_summary_update"
    || step === "memory_flush"
    || step === "marked_read"
  ) {
    return detail?.toLowerCase().includes("fail") ? "WARN" : "INFO";
  }

  return "DEBUG";
};

const formatStepMessage = (step: AgentRunStepKind, detail?: string) => {
  const label = stepLabels[step];

  if (!detail) return label;

  return `${label}: ${detail}`;
};

const memoryEventLevel = (event: MemoryTelemetryEvent): AdminLogLevel => {
  if (
    event === "idle_flush_failed"
    || event === "sweep_failed"
    || event === "prune_compact_failed"
    || event === "rolling_summary_failed"
  ) {
    return "WARN";
  }

  return "INFO";
};

const formatMemoryMessage = (payload: AgentRunMemoryIngestInput) => {
  const contactLabel = payload.contactId ?? "—";
  const eventLabel = payload.eventId ? ` event ${payload.eventId}` : "";

  switch (payload.event) {
    case "idle_flush":
      return `Memory idle flush: contact ${contactLabel}${eventLabel} — ${payload.messageCount ?? 0} msgs, ${payload.summaryChars ?? 0} summary chars`;
    case "idle_flush_failed":
      return `Memory idle flush failed: contact ${contactLabel}${eventLabel} — ${payload.error ?? "unknown error"}`;
    case "sweep_started":
      return `Memory sweep started (${payload.idleThresholdMs ?? 0}ms threshold, ${payload.sweepIntervalMs ?? 0}ms interval)`;
    case "sweep_failed":
      return `Memory sweep failed — ${payload.error ?? "unknown error"}`;
    case "prune_compact":
      return `Memory prune compact: contact ${contactLabel}${eventLabel} — ${payload.messageCount ?? 0} msgs, ${payload.summaryChars ?? 0} summary chars`;
    case "prune_compact_failed":
      return `Memory prune compact failed: contact ${contactLabel}${eventLabel} — ${payload.error ?? "unknown error"}`;
    case "rolling_summary_ok":
      return `Memory rolling summary updated${payload.runId ? ` — run ${shortId(payload.runId)}` : ""} — ${payload.summaryChars ?? 0} summary chars`;
    case "rolling_summary_failed":
      return `Memory rolling summary failed${payload.runId ? ` — run ${shortId(payload.runId)}` : ""} — ${payload.error ?? "unknown error"}`;
    default: {
      const _exhaustive: never = payload.event;
      return `Memory event: ${String(_exhaustive)}`;
    }
  }
};

const memoryDetailsFromPayload = (
  payload: AgentRunMemoryIngestInput,
): AdminLogDetails => ({
  kind: "memory",
  event: payload.event,
  contactId: payload.contactId,
  eventId: payload.eventId,
  messageCount: payload.messageCount,
  summaryChars: payload.summaryChars,
  deletedMessageCount: payload.deletedMessageCount,
  idleThresholdMs: payload.idleThresholdMs,
  sweepIntervalMs: payload.sweepIntervalMs,
  error: payload.error,
});

const resolvePhone = (
  payload: AgentRunIngestPayload,
  run: AgentRunRecord | null,
): string | undefined => {
  if (payload.action === "start" && payload.senderPhone) {
    return payload.senderPhone;
  }

  return run?.senderPhone ?? undefined;
};

const newLogEntry = (
  level: AdminLogLevel,
  message: string,
  runId?: string,
  phone?: string,
  details?: AdminLogDetails,
): AdminLogEntry => ({
  id: crypto.randomUUID(),
  ts: new Date().toISOString(),
  level,
  message,
  runId,
  phone,
  details,
});

export const parseLogDetailsFromMessage = (message: string): AdminLogDetails | null => {
  const contextMatch = message.match(
    /^Context ~([\d,]+) tok in — (\d+) msgs, (.+?), (\d+) memories, (event ctx|no event ctx)(?: · (.+))?$/,
  );

  if (contextMatch) {
    const summaryPart = contextMatch[3]!;
    const rollingSummary = summaryPart !== "no summary";
    const summaryChars = rollingSummary
      ? Number(summaryPart.replace(/,/g, "").replace(/\D/g, ""))
      : 0;

    return {
      kind: "context",
      estimatedTokens: Number(contextMatch[1]!.replace(/,/g, "")),
      conversationHistoryCount: Number(contextMatch[2]),
      rollingSummary,
      rollingSummaryChars: summaryChars,
      turbopufferMemoryCount: Number(contextMatch[4]),
      hasEventContext: contextMatch[5] === "event ctx",
      guestDetail: contextMatch[6],
    };
  }

  const toolMatch = message.match(/^Tool (.+?)\((.*)\) → ([\d,]+) chars(?: — error: (.+))?$/);

  if (toolMatch) {
    return {
      kind: "tool_call",
      name: toolMatch[1]!,
      argsPreview: toolMatch[2]!,
      resultChars: Number(toolMatch[3]!.replace(/,/g, "")),
      error: toolMatch[4],
    };
  }

  const stackedMatch = message.match(/^Stacked (\d+) inbound messages$/);

  if (stackedMatch) {
    return { kind: "stacked", stackedCount: Number(stackedMatch[1]) };
  }

  const completeMatch = message.match(/^Run \S+ completed in (.+)$/);

  if (completeMatch) {
    const latencyLabel = completeMatch[1]!;
    const latencyMs = latencyLabel.endsWith("ms")
      ? Number(latencyLabel.replace("ms", ""))
      : Math.round(Number(latencyLabel.replace("s", "")) * 1000);

    return { kind: "run_complete", latencyMs };
  }

  const failMatch = message.match(/^Run \S+ (failed|fallback): (.+)$/);

  if (failMatch) {
    return {
      kind: "run_fail",
      status: failMatch[1] as "failed" | "fallback",
      error: failMatch[2]!,
    };
  }

  const startMatch = message.match(/^Run \S+ started(?: — [^—]+)? — "(.+)"$/);

  if (startMatch) {
    return { kind: "run_start", inboundPreview: startMatch[1]! };
  }

  return null;
};

export const resolveLogDetails = (entry: AdminLogEntry): AdminLogDetails | null =>
  entry.details ?? parseLogDetailsFromMessage(entry.message);

export const logEntriesFromIngest = (
  payload: AgentRunIngestPayload,
  run: AgentRunRecord | null,
): AdminLogEntry[] => {
  const phone = resolvePhone(payload, run);

  switch (payload.action) {
    case "start": {
      const preview = payload.inboundPreview.trim() || "no preview";
      const phoneLabel = phone ? ` — ${phone}` : "";
      const contextSnapshot = payload.contextSnapshot;
      const entries = [
        newLogEntry(
          "INFO",
          `Run ${shortId(payload.runId)} started${phoneLabel} — "${preview}"`,
          payload.runId,
          phone,
          { kind: "run_start", inboundPreview: preview },
        ),
        newLogEntry(
          "DEBUG",
          formatContextSizeMessage(contextSnapshot),
          payload.runId,
          phone,
          {
            kind: "context",
            estimatedTokens: estimateContextTokens(contextSnapshot),
            conversationHistoryCount: contextSnapshot.conversationHistoryCount,
            rollingSummary: contextSnapshot.rollingSummary,
            rollingSummaryChars: contextSnapshot.rollingSummaryChars,
            turbopufferMemoryCount: contextSnapshot.turbopufferMemoryCount,
            hasEventContext: contextSnapshot.hasEventContext,
            guestDetail: contextSnapshot.guestDetail,
          },
        ),
      ];

      if (payload.stackedCount > 1) {
        entries.push(newLogEntry(
          "INFO",
          `Stacked ${payload.stackedCount} inbound messages`,
          payload.runId,
          phone,
          { kind: "stacked", stackedCount: payload.stackedCount },
        ));
      }

      return entries;
    }
    case "tool_call": {
      const level: AdminLogLevel = payload.tool.error ? "WARN" : "DEBUG";

      return [newLogEntry(
        level,
        formatToolMessage(payload.tool),
        payload.runId,
        phone,
        {
          kind: "tool_call",
          name: payload.tool.name,
          argsPreview: payload.tool.argsPreview,
          resultChars: payload.tool.resultChars,
          resultPreview: payload.tool.resultPreview,
          error: payload.tool.error,
        },
      )];
    }
    case "step": {
      const level = stepLevel(payload.step, payload.detail);

      return [newLogEntry(
        level,
        formatStepMessage(payload.step, payload.detail),
        payload.runId,
        phone,
        {
          kind: "step",
          step: payload.step,
          detail: payload.detail,
        },
      )];
    }
    case "complete": {
      return [
        newLogEntry(
          "INFO",
          `Run ${shortId(payload.runId)} completed in ${formatLatency(payload.latencyMs)}`,
          payload.runId,
          phone,
          { kind: "run_complete", latencyMs: payload.latencyMs },
        ),
      ];
    }
    case "fail": {
      const status = run?.status === "fallback" ? "fallback" : "failed";

      return [
        newLogEntry(
          "ERROR",
          `Run ${shortId(payload.runId)} ${status}: ${payload.error}`,
          payload.runId,
          phone,
          { kind: "run_fail", status, error: payload.error },
        ),
      ];
    }
    case "memory": {
      return [
        newLogEntry(
          memoryEventLevel(payload.event),
          formatMemoryMessage(payload),
          payload.runId,
          phone,
          memoryDetailsFromPayload(payload),
        ),
      ];
    }
    default: {
      const _exhaustive: never = payload;
      return [];
    }
  }
};

export const logEntryFromIngest = (
  payload: AgentRunIngestPayload,
  run: AgentRunRecord | null,
): AdminLogEntry | null => logEntriesFromIngest(payload, run)[0] ?? null;

export const filterLogEntries = (
  entries: AdminLogEntry[],
  input: { level?: AdminLogLevel; search?: string; phone?: string; limit?: number },
) => {
  const search = input.search?.trim().toLowerCase();
  const limit = input.limit ?? 200;

  return entries
    .filter((entry) => {
      if (input.level && entry.level !== input.level) {
        return false;
      }

      if (input.phone && !matchesPhoneFilter(entry.phone, input.phone)) {
        return false;
      }

      if (search) {
        const searchDigits = search.replace(/\D/g, "");

        return entry.message.toLowerCase().includes(search)
          || entry.runId?.toLowerCase().includes(search)
          || (searchDigits.length > 0 && (entry.phone?.replace(/\D/g, "").includes(searchDigits) ?? false));
      }

      return true;
    })
    .slice(0, limit);
};
